import type { LegalSection } from '../types/legal'

export const TERMS_UPDATED = 'September 28th 2026'

export const TERMS: LegalSection[] = [
  {
    h: '1. Overview',
    body: [
      {
        t: 'p',
        x: 'Welcome to MeetSynthia.ai ("Synthia," "we," "us," or "our"). These Terms and Conditions ("Terms") govern your access to and use of our website, products, and services (collectively, the "Platform"). By accessing or using the Platform, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, discontinue use immediately.',
      },
    ],
  },
  {
    h: '2. Nature of the Service',
    body: [
      {
        t: 'p',
        x: 'MeetSynthia.ai provides an AI-driven instruction layer that augments, governs, and optimizes user and agentic inputs ("prompts") for compatibility with large language models ("LLMs"). Synthia operates exclusively at the instruction layer. We do not generate, produce, or provide final AI outputs. All generative content, answers, and responses are produced entirely by external frontier AI models (e.g., OpenAI, Anthropic, Google, or your chosen enterprise LLM). Synthia\'s role is limited to preparing, governing, structuring, and verifying prompt instructions using configurable "guardrails," "attributes," "roles," and "workflows".',
      },
    ],
  },
  {
    h: '3. No Responsibility for AI Output',
    body: [
      {
        t: 'p',
        x: 'Because Synthia operates solely at the instruction layer and does not produce final answers, you explicitly acknowledge and agree that:',
      },
      {
        t: 'ul',
        items: [
          'Synthia is not responsible for any content, answers, or outputs generated by third-party AI systems.',
          'Synthia makes no warranties, representations, or guarantees as to the accuracy, completeness, legality, reliability, safety, or appropriateness of AI-generated outputs.',
          'Synthia cannot and does not make claims regarding security, PII handling, bias mitigation, or factual accuracy of final outputs—these are determined entirely by the frontier LLM provider.',
          "Regardless of whether instructions, guardrails, or workflows are customized by the client, suggested by Synthia's automated extraction engine, or pulled from historical logs, the client retains sole and absolute responsibility for reviewing, testing, and approving all instructions before execution.",
          'You agree to independently review, verify, and validate all AI-generated results before use, publication, or reliance.',
          'Any issues related to output quality, hallucinations, bias, toxicity, or compliance violations are the sole responsibility of the AI model provider and the user.',
        ],
      },
    ],
  },
  {
    h: '4. Enterprise Hosting and Data Handling',
    body: [
      {
        t: 'p',
        x: "When deployed in an enterprise environment, Synthia may be hosted privately within the client's infrastructure (self-hosted, on-premises, or VPC), or in Synthia's secure managed cloud environment.",
      },
      {
        t: 'p',
        x: 'For deployments hosted privately within the client’s infrastructure, the client acknowledges that Synthia does not access, view, or retain telemetry or prompt data unless explicitly granted written access for localized support purposes.',
      },
      { t: 'p', x: 'In all deployment models:' },
      {
        t: 'ul',
        items: [
          'The enterprise retains full ownership of its data, prompts, guardrails, workflows, and all proprietary configurations.',
          'Synthia processes data solely for the purpose of providing, improving, and supporting the Platform.',
          'Synthia will never sell, monetize, or use client data for model training, marketing, or competitive purposes.',
          'Client-specific proprietary information, including custom guardrails, unique workflows, domain expertise, and institutional knowledge extracted from your organization, will never be shared with third parties or other customers.',
          "Data sharing with third parties occurs only with the client's explicit written consent, or as required by law, court order, or lawful subpoena.",
        ],
      },
    ],
  },
  {
    h: '5. Marketplace and Contextual Intelligence Catalog',
    body: [
      {
        t: 'p',
        x: 'Synthia operates a marketplace of contextual intelligence that includes pre-built guardrails, roles, attributes, and workflows developed by Synthia, community-contributed and third-party components, and industry best practices. You acknowledge and agree that:',
      },
      {
        t: 'ul',
        items: [
          'Best practices, industry standards, and common governance patterns naturally overlap across organizations and cannot be considered proprietary to any single client.',
          'While your proprietary and confidential information will never be shared, Synthia may develop similar or related guardrails, workflows, or governance patterns for other clients based on industry knowledge, public information, and independent development.',
          'No client has exclusive rights to general concepts, approaches, or methodologies in AI governance.',
          'The marketplace may contain components similar to those developed for your organization, reflecting common industry needs rather than disclosure of your proprietary information.',
          'You cannot claim ownership or exclusive rights over industry-standard practices, common use cases, or generally applicable governance frameworks.',
          'Synthia commits that any components in the marketplace derived from work with specific clients will be sufficiently abstracted and generalized to remove client-specific proprietary information, trade secrets, and confidential business logic.',
        ],
      },
    ],
  },
  {
    h: '6. License and Acceptable Use',
    body: [
      {
        t: 'p',
        x: 'You are granted a limited, revocable, non-exclusive license to use the Platform for internal business purposes. You may not:',
      },
      {
        t: 'ul',
        items: [
          'Use the Platform for unlawful, infringing, or harmful activity.',
          'Reverse engineer, decompile, or modify any part of the Platform.',
          "Attempt to access another customer's data, configurations, or restricted systems.",
          'Use the Platform to generate content that violates applicable laws or third-party rights.',
        ],
      },
      { t: 'p', x: 'We reserve the right to suspend or terminate access for violations of these Terms.' },
    ],
  },
  {
    h: '7. Intellectual Property',
    body: [
      {
        t: 'p',
        x: 'All intellectual property in the Platform, including software, base guardrail logic, BrandKey™ technology, visual interfaces, documentation, and core architecture, belongs to MeetSynthia, Inc. or its licensors. Users retain ownership of their original data and prompts, custom guardrails and configurations they create, and proprietary workflows or institutional knowledge. By using the Platform, you grant Synthia a limited, non-exclusive right to process, host, and analyze your configurations solely to provide and improve the service. This license terminates upon account closure, subject to retention periods specified in our Privacy Policy.',
      },
    ],
  },
  {
    h: '8. Third-Party Integrations and Protocols',
    body: [
      { t: 'p', x: 'Synthia integrates with external AI providers and enterprise systems at your direction. You acknowledge that:' },
      {
        t: 'ul',
        items: [
          'We are not responsible for third-party systems, their availability, performance, uptime, or outputs.',
          'You must comply with all applicable third-party terms of service (e.g., OpenAI API Terms, Anthropic Terms, Google Cloud Terms).',
          "Integrations utilizing the Model Context Protocol (MCP) or other open-standard architectures depend entirely on third-party server environments. Synthia is not responsible for transport failures, security vulnerabilities, or localized configuration exploits originating within the client's localized protocol environment.",
          'Any issues with foundational AI model performance, connectivity, availability, or outputs must be addressed with the respective third-party provider.',
        ],
      },
    ],
  },
  {
    h: '9. Disclaimers',
    body: [
      {
        t: 'caps',
        x: 'THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND. WE EXPRESSLY DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, RELIABILITY, OR SECURITY. NO ADVICE OR INFORMATION OBTAINED FROM SYNTHIA SHALL CREATE ANY WARRANTY NOT EXPRESSLY STATED IN THESE TERMS. ALL AI OUTPUTS MUST BE REVIEWED BY QUALIFIED HUMANS BEFORE USE. AUTOMATED RELIANCE ON AI OUTPUTS IS DONE AT YOUR SOLE RISK.',
      },
    ],
  },
  {
    h: '10. Limitation of Liability',
    body: [
      { t: 'caps', x: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW:' },
      {
        t: 'ul',
        items: [
          'MeetSynthia shall not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses.',
          'Our total aggregate liability for any claim shall not exceed the total amount paid to MeetSynthia in the six (6) months immediately preceding the claim, or $100 USD, whichever is greater.',
          'This limitation applies regardless of the theory of liability (contract, tort, negligence, strict liability, or otherwise) and even if we have been advised of the possibility of such damages.',
          'Given that Synthia operates solely at the instruction layer and all final outputs are produced by third-party AI systems, MeetSynthia cannot be held responsible for any damages resulting from AI-generated content, including misinformation, bias, harmful content, privacy violations, regulatory non-compliance, or financial losses.',
        ],
      },
    ],
  },
  {
    h: '11. Indemnification',
    body: [
      {
        t: 'p',
        x: "You agree to indemnify, defend, and hold harmless MeetSynthia, its affiliates, officers, directors, employees, agents, and licensors from and against any and all claims, liabilities, damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising from your use or misuse of the Platform, your violation of these Terms, your violation of any rights of another party, content generated by third-party AI systems that you deploy, publish, or rely upon, or any breach of your representations or warranties herein.",
      },
    ],
  },
  {
    h: '12. Compliance with Data Protection Laws',
    body: [
      {
        t: 'p',
        x: 'Each party shall comply with all applicable data protection laws, including but not limited to the General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), Health Insurance Portability and Accountability Act (HIPAA) where applicable, and other relevant frameworks. When Synthia acts as a data processor, we process data only under the written instructions of the data controller (the client). Clients are responsible for ensuring their use of the Platform complies with all applicable regulations.',
      },
    ],
  },
  {
    h: '13. Termination',
    body: [
      {
        t: 'p',
        x: 'MeetSynthia may suspend or terminate your access at any time for violation of these Terms, non-payment of fees, engagement in fraudulent, harmful, or illegal activity, or via court order and legal requirement. Upon termination, you must immediately cease all use of the Platform. Data retention and deletion procedures are governed by our Privacy Policy and any applicable Data Processing Agreement.',
      },
    ],
  },
  {
    h: '14. Governing Law and Dispute Resolution',
    body: [
      {
        t: 'p',
        x: 'These Terms are governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions. Any disputes arising from or relating to these Terms or the Platform shall be resolved exclusively in the state or federal courts located in Delaware. You consent to the personal jurisdiction of such courts.',
      },
    ],
  },
  {
    h: '15. Changes to Terms',
    body: [
      {
        t: 'p',
        x: 'We reserve the right to modify these Terms at any time. Changes will be effective upon posting to the website with a new "Last Updated" date. Your continued use of the Platform after changes are posted constitutes acceptance of the revised Terms.',
      },
    ],
  },
  {
    h: '16. Severability',
    body: [
      {
        t: 'p',
        x: 'If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.',
      },
    ],
  },
  {
    h: '17. Entire Agreement',
    body: [
      {
        t: 'p',
        x: 'These Terms, together with our Privacy Policy and any applicable Data Processing Agreement, constitute the entire agreement between you and MeetSynthia regarding the Platform and supersede all prior or contemporaneous understandings and agreements.',
      },
    ],
  },
  {
    h: '18. Contact',
    body: [
      { t: 'p', x: 'For questions about these Terms: legal@meetsynthia.ai' },
      { t: 'p', x: 'MeetSynthia, Inc. Wilmington, Delaware, USA' },
    ],
  },
]
